import styled from "styled-components";
import { useEffect, useRef, useState } from "react";
import SearchAddrApi from "../../../User/Sign/Modal/SearchAddrApi";
import url, { SERVER_ADDRESS } from "../../../../Component/server";
import api2 from "../../../../Component/axios_Interceptor/api2";

import { ActionButton, ControlRow, Field, FieldLabel, FieldStack, FormInput, HelperText, Required } from "./SignupForm.styles";

interface Step2Props {
  BUSName: string;
  handleBUSName: (e: React.ChangeEvent<HTMLInputElement>) => void;
  BUSPostCode: string;
  BUSFirAddr: string;
  BUSSecAddr: string;
  setBUSPostCode: React.Dispatch<React.SetStateAction<string>>;
  setBUSFirAddr: React.Dispatch<React.SetStateAction<string>>;
  handleSecAddr: (e: React.ChangeEvent<HTMLInputElement>) => void;
  BUSImage: string[];
  uploadBUSImage: (data: string) => void;
  delBUSImage: (data: string, index: number) => void;
}

const Step2 = ({
  BUSName,
  handleBUSName,
  BUSPostCode,
  BUSFirAddr,
  BUSSecAddr,
  setBUSPostCode,
  setBUSFirAddr,
  handleSecAddr,
  BUSImage,
  uploadBUSImage,
  delBUSImage,
}: Step2Props) => {
  const openSearchAddr = () => {
    SearchAddrApi({ setFirAddr: setBUSFirAddr, setPostCode: setBUSPostCode });
  };

  // 사진 업로드
  const picPreviewRef = useRef<HTMLInputElement | null>(null);
  const uploadRequest = useRef<AbortController | null>(null);
  const [uploading, setUploading] = useState(false);
  const [uploadError, setUploadError] = useState("");
  useEffect(() => () => {uploadRequest.current?.abort();}, []);
  const insert_pic = () => {
    if (!uploadRequest.current && picPreviewRef.current) {
      picPreviewRef.current.click();
    }
  };

  const allowedExtensions = [".png", ".jpg", ".jpeg", ".pdf"];
  const renderPic = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file || uploadRequest.current) return;
    if (!allowedExtensions.some(ext => file.name.toLowerCase().endsWith(ext))) {
      setUploadError("PNG, JPG 또는 PDF 파일을 첨부해주세요."); return;
    }
    if (!file.size || file.size > 10 * 1024 * 1024) {
      setUploadError("사업자등록증은 0바이트보다 크고 10MB 이하여야 합니다."); return;
    }
    void setting_pic(file);
  };

  const setting_pic = async (file: File) => {
    if (uploadRequest.current) return;
    const controller = new AbortController();
    uploadRequest.current = controller;
    setUploading(true); setUploadError("");
    try {
      const form = new FormData();
      form.append("file", file);
      const res = await api2.post(url.Communication_url.upload_license, form, {
        signal: controller.signal, headers: { "Content-Type": "multipart/form-data" },
      });
      if (controller.signal.aborted) return;
      if (typeof res.data?.file_path !== "string" || !res.data.file_path.trim()) throw new Error("사업자등록증 저장 결과를 확인하지 못했습니다.");
      uploadBUSImage(res.data.file_path);
    } catch (error: any) {
      if (controller.signal.aborted) return;
      const message = error?.response?.data?.message;
      setUploadError(typeof message === "string" ? message : "사업자등록증을 업로드하지 못했습니다. 연결 상태를 확인하고 다시 첨부해주세요.");
    } finally {
      if (!controller.signal.aborted) {uploadRequest.current = null; setUploading(false);}
    }
  };

  return (
    <FieldStack>
      <Field>
        <FieldLabel htmlFor="seller-business-name">사업자명 <Required>*</Required></FieldLabel>
        <FormInput id="seller-business-name" type="text" placeholder="사업자명을 입력해주세요." value={BUSName} onChange={handleBUSName} />
      </Field>
      <Field>
        <FieldLabel htmlFor="seller-business-address">사업자 주소 <Required>*</Required></FieldLabel>
        <ControlRow>
          <FormInput disabled type="text" aria-label="우편번호" placeholder="우편번호" value={BUSPostCode} />
          <ActionButton type="button" onClick={openSearchAddr}>검색</ActionButton>
        </ControlRow>
        <FormInput id="seller-business-address" disabled type="text" placeholder="주소" value={BUSFirAddr} />
        <FormInput type="text" aria-label="상세주소" placeholder="상세주소를 입력해주세요." value={BUSSecAddr} onChange={handleSecAddr} />
      </Field>
      <Field>
        <FieldLabel as="div">사업자등록증 <Required>*</Required></FieldLabel>
        <ImageListDiv>
          {BUSImage.length === 0 && (
            <UploadButton type="button" aria-label="사업자등록증 첨부하기" disabled={uploading} onClick={insert_pic}>
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" aria-hidden="true">
                <path d="M12 5v14M5 12h14" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
              </svg>
            </UploadButton>
          )}
          {BUSImage.map((item, index) => (
            <ImageDiv key={index}>
              <DelBtn type="button" aria-label="사업자등록증 삭제" disabled={uploading} onClick={() => {
                if (!uploadRequest.current) delBUSImage(item, index);
              }}>
                <svg width="20" height="20" viewBox="0 0 20 20" fill="none" aria-hidden="true">
                  <path d="m6 6 8 8m0-8-8 8" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
                </svg>
              </DelBtn>
              <Image alt="등록한 사업자등록증"
                onError={() => setUploadError("저장된 사업자등록증 이미지를 불러오지 못했습니다. 다시 첨부해주세요.")}
                src={item.includes(".pdf") ? require("../../../../Image/ic_pdf.png") : SERVER_ADDRESS + "openfile/" + item} />
            </ImageDiv>
          ))}
        </ImageListDiv>
        {uploading && <HelperText role="status">사업자등록증을 업로드하는 중입니다.</HelperText>}
        {uploadError && <HelperText role="alert" style={{ color: "var(--color-text-danger)" }}>{uploadError}</HelperText>}
        <input style={{ display: "none" }} ref={picPreviewRef} type="file" accept="image/png,image/jpeg,application/pdf"
          aria-label="사업자등록증 첨부" disabled={uploading} onChange={renderPic} />
      </Field>
    </FieldStack>
  );
};

export default Step2;

const ImageListDiv = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: var(--space-4);
  padding: var(--space-3) 0;
`;
const ImageDiv = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  position: relative;
  width: calc(var(--space-12) * 2);
  height: calc(var(--space-12) * 2);
  border: 1px solid var(--color-border-default);
  border-radius: var(--radius-md);
  background: var(--color-bg-surface);
`;
const UploadButton = styled.button`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: calc(var(--space-12) * 2);
  height: calc(var(--space-12) * 2);
  padding: 0;
  border: 1px solid var(--color-border-default);
  border-radius: var(--radius-md);
  background: var(--color-bg-surface);
  color: var(--color-text-secondary);
  cursor: pointer;
  &:hover:not(:disabled) { border-color: var(--color-brand); }
  &:disabled { color: var(--color-text-disabled); cursor: not-allowed; }
`;
const Image = styled.img`
  width: 100%;
  height: 100%;
  object-fit: contain;
  border-radius: var(--radius-md);
`;
const DelBtn = styled.button`
  position: absolute;
  top: calc(var(--space-3) * -1);
  right: calc(var(--space-3) * -1);
  display: grid;
  place-items: center;
  width: var(--touch-target);
  height: var(--touch-target);
  padding: 0;
  border: 1px solid var(--color-border-default);
  border-radius: var(--radius-pill);
  background: var(--color-bg-elevated);
  color: var(--color-text-primary);
  cursor: pointer;
  &:disabled { color: var(--color-text-disabled); cursor: not-allowed; }
`;
